import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Mario's portrait photo
const marioPhoto = "https://customer-assets.emergentagent.com/job_26230bfe-3ad4-44db-9ae5-7183ab1c05bc/artifacts/zrgeytqv_4123878C-9189-4242-B8A0-B396518F93D2.PNG";

const principlesData = {
  EN: [
    { title: "Preventive Focus", desc: "Proactive monitoring and maintenance to identify and address issues before they escalate into costly problems." },
    { title: "Transparent Communication", desc: "Regular updates, detailed reporting, and clear documentation of all activities and expenditures." },
    { title: "Reliable Execution", desc: "Consistent follow-through on commitments, with attention to detail and quality in every task." },
    { title: "Cost Control", desc: "Quote comparison, budget monitoring, and value-focused recommendations for all services." },
    { title: "Local Expertise", desc: "Established relationships with reliable contractors and deep knowledge of local regulations." },
    { title: "Long-Term View", desc: "Property management decisions made with long-term preservation and value in mind." }
  ],
  ES: [
    { title: "Enfoque Preventivo", desc: "Monitorización proactiva y mantenimiento para identificar y abordar problemas antes de que escalen a problemas costosos." },
    { title: "Comunicación Transparente", desc: "Actualizaciones regulares, informes detallados y documentación clara de todas las actividades y gastos." },
    { title: "Ejecución Fiable", desc: "Seguimiento consistente de compromisos, con atención al detalle y calidad en cada tarea." },
    { title: "Control de Costes", desc: "Comparación de presupuestos, monitorización de gastos y recomendaciones enfocadas en valor para todos los servicios." },
    { title: "Experiencia Local", desc: "Relaciones establecidas con contratistas fiables y profundo conocimiento de las regulaciones locales." },
    { title: "Visión a Largo Plazo", desc: "Decisiones de gestión de propiedades tomadas con preservación y valor a largo plazo en mente." }
  ]
};

const AboutPage = () => {
  const { language, t } = useLanguage();
  const principles = principlesData[language];

  const areas = ['Calvià', 'Santa Ponsa', "Port d'Andratx", 'Camp de Mar', 'Costa de la Calma', 'Sol de Mallorca', 'Portals Nous', 'Bendinat', 'Son Vida', 'Palmanova', 'Illetes', 'Cas Català'];

  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About Us | Property Management Mallorca | Balearic Home Solutions</title>
        <meta name="description" content="Balearic Home Solutions is a private property management firm based in Calvià, Mallorca. Founded by Mario Alvarez, serving international homeowners since 2016." />
        <link rel="canonical" href="https://balearichomesolutions.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {t.aboutPage.label}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {t.aboutPage.title}
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed">
              {t.aboutPage.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Section with Mario's Photo */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="relative max-w-md mx-auto lg:mx-0">
                <img
                  src={marioPhoto}
                  alt="Mario Alvarez - Founder of Balearic Home Solutions"
                  className="w-full h-auto object-cover object-top max-h-[470px] md:max-h-[580px]"
                  data-testid="mario-photo"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-navy text-white p-4">
                  <p className="font-heading text-lg">Mario Alvarez</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider">
                    {language === 'ES' ? 'Fundador · Gestor de Propiedades' : 'Founder · Property Manager'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
                {t.aboutPage.companyTitle}
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>{t.aboutPage.companyText1}</p>
                <p>{t.aboutPage.companyText2}</p>
                <p>{t.aboutPage.companyText3}</p>
              </div>
              
              <div className="mt-8 p-6 bg-stone-50 border-l-2 border-navy">
                <p className="font-heading text-xl text-navy italic">
                  {t.aboutPage.tagline}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
                {t.aboutPage.serviceAreaTitle}
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>{t.aboutPage.serviceAreaText1}</p>
                <p>{t.aboutPage.serviceAreaText2}</p>
              </div>
              
              <div className="mt-8 flex items-start gap-3">
                <MapPin size={20} className="text-navy mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-navy">Calvià, Mallorca</p>
                  <p className="text-sm text-stone-500">Balearic Islands · Spain</p>
                </div>
              </div>
            </div>
            
            <div className="bg-navy p-8 md:p-12">
              <h3 className="font-heading text-2xl text-white mb-6">
                {t.aboutPage.areasWeServe}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {areas.map((area, index) => (
                  <p key={index} className="text-white/70 text-sm flex items-center gap-2">
                    <span className="w-1 h-1 bg-white/50 rounded-full" />
                    {area}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
              {t.aboutPage.principlesTitle}
            </h2>
            <p className="text-stone-600 leading-relaxed">
              {t.aboutPage.principlesDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {principles.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border-l-2 border-navy pl-6"
              >
                <h3 className="font-heading text-lg text-navy mb-2">
                  {value.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-6">
              {t.aboutPage.ctaTitle}
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              {t.aboutPage.ctaDesc}
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="bg-white text-navy px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-stone-100 transition-all inline-flex items-center gap-2"
            >
              {t.servicesPage.getInTouch}
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
